import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTournament, computeHighBreak, computeAvgShotTime } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import type { Tournament, Match, Player, Round, Shot, BallColour } from '@shootout-comps/types';
import { SnookerBallSpinner } from '../components/SnookerBall';
import { FrameBreakdownList } from '../components/SpectatorMatchView';

const BALL_CSS: Record<BallColour, string> = {
  red: '#c0392b', yellow: '#f1c40f', green: '#27ae60',
  brown: '#795548', blue: '#2980b9', pink: '#e91e8c',
  black: '#222', white: '#f5f0e8',
};


const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"%3E%3Ccircle cx="32" cy="32" r="32" fill="%23236038"/%3E%3Ccircle cx="32" cy="24" r="10" fill="%23c9a84c" opacity="0.7"/%3E%3Cellipse cx="32" cy="52" rx="16" ry="10" fill="%23c9a84c" opacity="0.7"/%3E%3C/svg%3E';

// Fixed card height so the bracket-tree stagger math below (mirrors BracketPage.tsx's
// RoundColumn) can position each round's matches to line up with their two feeder matches
// from the previous round, instead of everything stacking flush to the top.
const CARD_H   = 130;
const CARD_GAP = 12;

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function Avatar({ player, size = 40 }: { player: Player; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
      border: '2px solid rgba(201,168,76,0.3)', background: '#1a4a2e',
    }}>
      <img
        src={player.profilePictureUrl ? player.profilePictureUrl : PLACEHOLDER}
        alt={player.name}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </div>
  );
}

interface HistoricTournamentPageProps {
  tournamentId: string;
  onBack: () => void;
}

export default function HistoricTournamentPage({ tournamentId, onBack }: HistoricTournamentPageProps) {
  const { fetchHistoricTournament } = useTournament();
  const { t } = useLang();
  const { matchId } = useParams<{ matchId?: string }>();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchHistoricTournament(tournamentId)
      .then(setTournament)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tournamentId, fetchHistoricTournament]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', color: 'var(--chalk-dim)' }}>
        <div style={{ textAlign: 'center' }}>
          <SnookerBallSpinner size={36} />
          <p style={{ marginTop: '0.5rem' }}>{t.loadingTournament}</p>
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '0 1rem' }}>
        <button onClick={onBack} style={backBtnStyle}>← Back</button>
        <div style={{ marginTop: '1rem', background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '1rem', color: '#e74c3c' }}>
          {error ?? t.tournamentNotFound}
        </div>
      </div>
    );
  }

  if (matchId) {
    const selectedMatch = tournament.rounds.flatMap(r => r.matches).find(m => m._id === matchId);
    if (!selectedMatch) {
      return (
        <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '0 1rem' }}>
          <button onClick={() => navigate(`/tournaments/${tournamentId}`)} style={backBtnStyle}>← Back</button>
          <div style={{ marginTop: '1rem', background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '1rem', color: '#e74c3c' }}>
            Match not found
          </div>
        </div>
      );
    }
    return (
      <MatchDetailView
        tournamentId={tournamentId}
        match={selectedMatch}
        onBack={() => navigate(`/tournaments/${tournamentId}`)}
      />
    );
  }

  const champion = tournament.status === 'complete'
    ? tournament.rounds[tournament.rounds.length - 1]?.matches[0]?.winner
    : null;
  const realPlayers = tournament.players.filter((p) => !p.isBye);
  const createdAt = new Date((tournament as unknown as { createdAt: string }).createdAt).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div style={{ padding: 'var(--page-pad)' }}>
      <button onClick={onBack} style={backBtnStyle}>{t.backToHistory}</button>

      {/* Tournament header */}
      <div style={{
        maxWidth: '900px', margin: '1.25rem auto 0',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px',
        padding: '1.5rem', marginBottom: '1.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '2.2rem', color: 'var(--gold)', lineHeight: 1 }}>
              {tournament.name}
            </h2>
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginTop: '0.3rem' }}>
              📅 {createdAt} · 👥 {realPlayers.length} players
            </p>
          </div>
          {champion && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem' }}>🏆</div>
              <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Champion</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.35rem' }}>
                <Avatar player={champion as Player} size={32} />
                <span style={{ fontWeight: 700, color: 'var(--gold)', fontSize: '0.95rem' }}>{(champion as Player).name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Player grid */}
        {realPlayers.length > 0 && (
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '1rem' }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.6rem' }}>
              {t.participants}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {realPlayers.sort((a, b) => a.seed - b.seed).map((p) => (
                <div key={p._id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: '20px', padding: '0.25rem 0.6rem 0.25rem 0.25rem',
                  fontSize: '0.78rem',
                }}>
                  <Avatar player={p} size={22} />
                  <span>{p.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bracket */}
      {tournament.rounds.length > 0 && (
        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: '1rem', padding: '0 var(--page-pad) 1rem', alignItems: 'flex-start', minWidth: 'max-content', margin: '0 auto' }}>
            {tournament.rounds.map((round, roundIdx) => (
              <HistoricRoundColumn
                key={round.round}
                round={round}
                roundIdx={roundIdx}
                onSelectMatch={(m) => navigate(`/tournaments/${tournamentId}/match/${m._id}`)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Round column ─────────────────────────────────────────────────────────────

function HistoricRoundColumn({ round, roundIdx, onSelectMatch }: { round: Round; roundIdx: number; onSelectMatch: (m: Match) => void }) {
  const done = round.matches.every((m) => m.status === 'complete');

  // Stagger each round's matches so they line up with the midpoint of the two feeder
  // matches that produced them — same absolute-positioning approach as BracketPage.tsx's
  // RoundColumn, just with the fixed CARD_H sized for this page's simpler read-only card.
  const unitSpacing   = CARD_H + CARD_GAP;
  const centreSpacing = unitSpacing * Math.pow(2, roundIdx);
  const firstTop      = centreSpacing / 2 - CARD_H / 2;
  const colH          = firstTop + round.matches.length * CARD_H + (round.matches.length - 1) * (centreSpacing - CARD_H);

  return (
    <div style={{ minWidth: '210px', maxWidth: '230px', flexShrink: 0 }}>
      <div style={{
        textAlign: 'center', marginBottom: '0.85rem', paddingBottom: '0.6rem',
        borderBottom: `2px solid ${done ? 'var(--gold)' : 'rgba(255,255,255,0.1)'}`,
      }}>
        <h3 style={{
          fontFamily: 'Bebas Neue', fontSize: '1.3rem',
          color: done ? 'var(--gold)' : 'var(--chalk)', letterSpacing: '0.04em',
        }}>
          {round.name}
        </h3>
        <span style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)' }}>
          {round.matches.filter((m) => m.status === 'complete').length}/{round.matches.length} played
        </span>
      </div>
      <div style={{ position: 'relative', height: colH }}>
        {round.matches.map((match, i) => (
          <div key={match._id} style={{ position: 'absolute', top: firstTop + i * centreSpacing, left: 0, right: 0 }}>
            <HistoricMatchCard match={match} onSelect={() => onSelectMatch(match)} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Match card (read-only bracket view) ─────────────────────────────────────

function HistoricMatchCard({ match, onSelect }: { match: Match; onSelect: () => void }) {
  const { player1, player2, winner, score1, score2, status } = match;
  const complete = status === 'complete';
  const isBye = player2?.isBye;
  const clickable = complete && !isBye;
  const isMultiFrame = (match.bestOf ?? 1) > 1;
  const displayScore1 = isMultiFrame ? (match.frameWins1 ?? 0) : score1;
  const displayScore2 = isMultiFrame ? (match.frameWins2 ?? 0) : score2;

  return (
    <div
      onClick={() => clickable && onSelect()}
      style={{
        background: complete
          ? 'linear-gradient(135deg, rgba(201,168,76,0.07), rgba(255,255,255,0.02))'
          : 'rgba(255,255,255,0.03)',
        border: `1px solid ${complete ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: '8px', padding: '0.55rem',
        cursor: clickable ? 'pointer' : 'default', transition: 'all 0.15s',
        height: `${CARD_H}px`, boxSizing: 'border-box', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}
      className={clickable ? 'hist-card' : ''}
    >
      <HistoricPlayerRow player={player1} isWinner={winner?._id === player1._id} score={displayScore1} complete={complete} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.1rem 0.5rem' }}>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
        <span style={{ fontSize: '0.58rem', color: 'var(--chalk-dim)', letterSpacing: '0.1em' }}>{isMultiFrame ? `BO${match.bestOf}` : 'VS'}</span>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
      </div>
      {player2
        ? <HistoricPlayerRow player={player2} isWinner={winner?._id === player2._id} score={displayScore2} complete={complete} />
        : <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', padding: '0.3rem 0.5rem', fontStyle: 'italic' }}>BYE</div>
      }
      {/* Always reserve this row's height (like MatchCard's status row) so every card in a
          round is exactly CARD_H tall regardless of clickability, keeping the stagger math above accurate. */}
      <div style={{ textAlign: 'center', marginTop: '0.4rem', height: '14px' }}>
        {clickable && <span style={{ fontSize: '0.62rem', color: 'var(--gold)', letterSpacing: '0.06em' }}>→ View details</span>}
      </div>
      <style>{`.hist-card:hover{transform:translateY(-1px);box-shadow:0 4px 14px rgba(0,0,0,0.3)}`}</style>
    </div>
  );
}

function HistoricPlayerRow({ player, isWinner, score, complete }: { player: Player; isWinner: boolean; score: number | null; complete: boolean }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.4rem',
      background: isWinner ? 'rgba(201,168,76,0.1)' : 'transparent',
      borderRadius: '5px', opacity: complete && !isWinner ? 0.45 : 1,
    }}>
      <Avatar player={player} size={24} />
      <span style={{
        flex: 1, fontSize: '0.82rem', fontWeight: isWinner ? 600 : 400,
        color: isWinner ? 'var(--gold)' : 'var(--chalk)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {player.name}
      </span>
      {complete && score !== null && (
        <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.05rem', color: isWinner ? 'var(--gold)' : 'var(--chalk-dim)', minWidth: '20px', textAlign: 'right' }}>
          {score}
        </span>
      )}
      {isWinner && <span style={{ fontSize: '0.6rem' }}>✓</span>}
    </div>
  );
}

// ─── Match detail view (shot-by-shot history) ─────────────────────────────────

interface MatchDetailViewProps {
  tournamentId: string;
  match: Match;
  onBack: () => void;
}

function MatchDetailView({ tournamentId, match: initialMatch, onBack }: MatchDetailViewProps) {
  const { t } = useLang();
  const { fetchHistoricMatch } = useTournament();
  const [match, setMatch] = useState<Match>(initialMatch);
  const [loading, setLoading] = useState(false);

  // Resolve shots — prefer the permanent shotHistory archive, fall back to liveState.shots
  const matchAny = match as unknown as { shotHistory?: Shot[] };
  const shots: Shot[] =
    (matchAny.shotHistory && matchAny.shotHistory.length > 0)
      ? matchAny.shotHistory
      : (match.liveState?.shots ?? []);

  useEffect(() => {
    // Fetch full match detail if neither shotHistory nor liveState is present yet
    const needsFetch = !matchAny.shotHistory?.length && !initialMatch.liveState;
    if (needsFetch) {
      setLoading(true);
      fetchHistoricMatch(tournamentId, initialMatch._id)
        .then(setMatch)
        .catch(() => null)
        .finally(() => setLoading(false));
    }
  }, [tournamentId, initialMatch._id, initialMatch.liveState, fetchHistoricMatch]);

  const p1 = match.player1 as Player;
  const p2 = match.player2 as Player | null;
  const winner = match.winner as Player | null;
  const ls = match.liveState;
  const frames = match.frames ?? [];
  const isMultiFrame = (match.bestOf ?? 1) > 1;
  // `shots` above is the final/current frame only for a multi-frame match (shotHistory mirrors
  // the latest frame) — high break/avg shot time here are scoped to that frame accordingly.
  // For the whole-match figure, fold in every archived frame's own high break too.
  const p1HighBreak = computeHighBreak(shots, String(p1._id));
  const p2HighBreak = p2 ? computeHighBreak(shots, String(p2._id)) : 0;
  const p1MatchHighBreak = Math.max(p1HighBreak, ...frames.map(f => f.highBreak1));
  const p2MatchHighBreak = Math.max(p2HighBreak, ...frames.map(f => f.highBreak2));
  const p1AvgShotTime = computeAvgShotTime(shots, String(p1._id));
  const p2AvgShotTime = p2 ? computeAvgShotTime(shots, String(p2._id)) : 0;

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      <button onClick={onBack} style={backBtnStyle}>{t.backToDraw}</button>

      {/* Score summary */}
      <div style={{
        marginTop: '1.25rem',
        background: 'linear-gradient(135deg, rgba(201,168,76,0.08), rgba(255,255,255,0.02))',
        border: '1px solid rgba(201,168,76,0.2)', borderRadius: '14px', padding: '1.5rem',
        marginBottom: '1.5rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <ResultPlayerPanel player={p1} score={isMultiFrame ? (match.frameWins1 ?? 0) : (match.score1 ?? (ls?.score1 ?? 0))} isWinner={winner?._id === p1._id} highBreak={isMultiFrame ? p1MatchHighBreak : p1HighBreak} avgShotTime={p1AvgShotTime} />
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.1rem', color: 'var(--chalk-dim)', letterSpacing: '0.1em' }}>FINAL</div>
            {isMultiFrame && (
              <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', marginTop: '0.2rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Best of {match.bestOf}
              </div>
            )}
            {ls && (
              <div style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', marginTop: '0.3rem' }}>
                ⏱ {formatTime(ls.elapsedSeconds)} {t.timePlayed}
              </div>
            )}
            <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginTop: '0.15rem' }}>
              {shots.length} shots{isMultiFrame ? ' (final frame)' : ''}
            </div>
          </div>
          {p2 && <ResultPlayerPanel player={p2} score={isMultiFrame ? (match.frameWins2 ?? 0) : (match.score2 ?? (ls?.score2 ?? 0))} isWinner={winner?._id === p2._id} highBreak={isMultiFrame ? p2MatchHighBreak : p2HighBreak} avgShotTime={p2AvgShotTime} />}
        </div>
        {isMultiFrame && p2 && <FrameBreakdownList frames={frames} p1={p1} p2={p2} />}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '1rem', fontSize: '0.85rem' }}>
          Loading shot history…
        </div>
      )}

      {/* Shot-by-shot history */}
      {shots.length > 0 && (
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>
            {isMultiFrame ? `Final Frame Shot History (${shots.length} shots)` : `Shot History (${shots.length} shots)`}
          </div>

          {/* Running score header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr auto 1fr',
            gap: '0.5rem', marginBottom: '0.5rem', padding: '0 0.5rem',
          }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', fontWeight: 600 }}>
              {p1.name.split(' ')[0]}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', textAlign: 'center' }}>Shot</div>
            {p2 && <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', fontWeight: 600, textAlign: 'right' }}>
              {p2.name.split(' ')[0]}
            </div>}
          </div>

          <ShotTimeline
            shots={shots}
            player1Id={ls?.player1Id ?? match.player1._id}
            player2Id={ls?.player2Id ?? (match.player2?._id ?? '')}
            p1={p1}
            p2={p2}
          />
        </div>
      )}

      {shots.length === 0 && !loading && (
        <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '2rem', fontSize: '0.85rem' }}>
          No shot data recorded for this match.
        </div>
      )}
    </div>
  );
}

function ResultPlayerPanel({ player, score, isWinner, highBreak, avgShotTime }: { player: Player; score: number; isWinner: boolean; highBreak: number; avgShotTime: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
      <Avatar player={player} size={56} />
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontWeight: 600, fontSize: '0.9rem', maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {player.name}
        </div>
        {isWinner && <div style={{ fontSize: '0.6rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>🏆 Winner</div>}
      </div>
      <div style={{ fontFamily: 'Bebas Neue', fontSize: '2.8rem', lineHeight: 1, color: isWinner ? 'var(--gold)' : 'var(--chalk)' }}>
        {score}
      </div>
      {highBreak > 0 && <div style={{ color: 'var(--chalk-dim)', fontSize: '0.75rem' }}>High break: {highBreak}</div>}
      {avgShotTime > 0 && <div style={{ color: 'var(--chalk-dim)', fontSize: '0.75rem' }}>Avg shot: {Math.round(avgShotTime)}s</div>}
    </div>
  );
}

// ─── Shot timeline ────────────────────────────────────────────────────────────

function ShotTimeline({ shots, player1Id, player2Id: _player2Id, p1: _p1, p2: _p2 }: {
  shots: Shot[]; player1Id: string; player2Id: string; p1: Player; p2: Player | null;
}) {
  // Build running scores
  let r1 = 0, r2 = 0;
  const rows = shots.map((shot, i) => {
    const isP1 = shot.playerId === player1Id;
    if (shot.isFoul) {
      // penalty to opponent
      if (isP1) r2 += (shot.foulPenalty ?? 4);
      else r1 += (shot.foulPenalty ?? 4);
    } else if (shot.points > 0) {
      if (isP1) r1 += shot.points;
      else r2 += shot.points;
    }
    return { shot, isP1, runningR1: r1, runningR2: r2, idx: i + 1 };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '480px', overflowY: 'auto' }}>
      {rows.map(({ shot, isP1, runningR1, runningR2, idx }) => (
        <div key={idx} style={{
          display: 'grid', gridTemplateColumns: '1fr auto 1fr',
          gap: '0.5rem', alignItems: 'center',
          padding: '0.4rem 0.5rem',
          background: shot.isFoul ? 'rgba(192,57,43,0.07)' : 'rgba(255,255,255,0.025)',
          border: `1px solid ${shot.isFoul ? 'rgba(192,57,43,0.15)' : 'rgba(255,255,255,0.05)'}`,
          borderRadius: '6px',
        }}>
          {/* P1 side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', opacity: isP1 ? 1 : 0.3 }}>
            {isP1 && <ShotBadge shot={shot} />}
            <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.1rem', color: 'var(--gold)', marginLeft: 'auto' }}>
              {runningR1}
            </span>
          </div>

          {/* Shot number */}
          <div style={{ textAlign: 'center', minWidth: '28px' }}>
            <span style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.25)' }}>{idx}</span>
          </div>

          {/* P2 side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', opacity: !isP1 ? 1 : 0.3 }}>
            <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.1rem', color: 'var(--gold)' }}>
              {runningR2}
            </span>
            {!isP1 && <ShotBadge shot={shot} />}
          </div>
        </div>
      ))}
    </div>
  );
}

function ShotBadge({ shot }: { shot: Shot }) {
  const { t } = useLang();
  if (shot.isFoul) {
    return (
      <span style={{
        fontSize: '0.68rem', background: 'rgba(192,57,43,0.2)', color: '#e74c3c',
        border: '1px solid rgba(192,57,43,0.3)', borderRadius: '4px',
        padding: '0.15rem 0.4rem', whiteSpace: 'nowrap',
      }}>
        ⚠ Foul +{shot.foulPenalty}
      </span>
    );
  }
  if (shot.ballPotted) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
        <div style={{
          width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
          background: BALL_CSS[shot.ballPotted],
          border: '1px solid rgba(255,255,255,0.2)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
        }} />
        <span style={{ fontSize: '0.68rem', color: 'var(--gold)', whiteSpace: 'nowrap' }}>
          +{shot.points}
        </span>
        {shot.isFreeBall && (
          <span title={t.freeBallSnookeredMsg} style={{
            fontSize: '0.6rem', fontWeight: 700, color: 'var(--gold)',
            background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)',
            borderRadius: '3px', padding: '0.05rem 0.3rem', whiteSpace: 'nowrap',
          }}>
            🔴 {t.freeBallLabel}
          </span>
        )}
      </div>
    );
  }
  return (
    <span style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', whiteSpace: 'nowrap' }}>
      ↩ miss
    </span>
  );
}

const backBtnStyle: React.CSSProperties = {
  background: 'transparent', color: 'var(--chalk-dim)',
  border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px',
  padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer',
};
